import React from "react";
import { useStatsTooltip } from "./StatsTooltip";
import { fmtInt, fmtMoneyCompact, fmtPct0 } from "./statsFormat";

// Below this share a segment is too narrow to carry its own percentage label.
const MIN_LABELLED_SHARE = 0.12;

/**
 * "Canal de reserva": where bookings come in from (Web, App, Mostrador / carga manual) as a 100 %
 * stacked bar with a row per channel, followed by extra sections of label / value / muted rows that
 * each screen supplies (how the money splits for courts; how clients choose, collections and lead
 * time for Beauty).
 *
 * The stack is an image for assistive tech (its aria-label lists every share); the rows below carry
 * the same numbers as text.
 *
 * @param {{channels: Array<{key: string, label: string, color: string, ink: string, count: number,
 *   revenue: number, share: number|null}>, subtitle: string, unit: string, emptyText: string,
 *   sections: Array<{title: string, rows: Array<{key: string, label: string, value: string, muted: string}>}>}} props
 *   `channels` comes from a `channelShares`; `unit` is what is counted ("reservas", "turnos").
 */
export default function ChannelPanel({ channels, subtitle, unit, emptyText, sections }) {
  const tooltip = useStatsTooltip();
  const hasBookings = channels.some((c) => c.count > 0);

  return (
    <section className="stats-panel" aria-labelledby="stats-channel-title">
      <div className="stats-panel__head">
        <div>
          <h2 className="stats-panel__title" id="stats-channel-title">
            Canal de reserva
          </h2>
          <p className="stats-panel__sub">{subtitle}</p>
        </div>
      </div>

      {hasBookings ? (
        <>
          <div className="stats-stack" role="img" aria-label={channels.map((c) => `${c.label}: ${fmtPct0(c.share)}`).join(", ")}>
            {channels
              .filter((c) => c.share > 0)
              .map((c) => (
                <div
                  key={c.key}
                  style={{ flex: `${c.share} 1 0`, background: c.color, color: c.ink }}
                  onPointerMove={(event) =>
                    tooltip.show(
                      c.label,
                      [{ value: fmtPct0(c.share), label: `${fmtInt(c.count)} ${unit}`, color: c.color, rect: true }],
                      event.clientX,
                      event.clientY
                    )
                  }
                  onPointerLeave={tooltip.hide}
                >
                  {c.share >= MIN_LABELLED_SHARE ? fmtPct0(c.share) : null}
                </div>
              ))}
          </div>
          <div className="stats-rows">
            {channels.map((c) => (
              <div key={c.key} className="stats-rows__item">
                <span className="stats-rows__name">
                  <i className="stats-key-rect" style={{ background: c.color }} aria-hidden="true" />
                  {c.label}
                </span>
                <span className="stats-rows__num">{`${fmtInt(c.count)} ${unit}`}</span>
                <span className="stats-rows__muted">{fmtMoneyCompact(c.revenue)}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="stats-footnote">{emptyText}</p>
      )}

      {sections.map((section) => (
        <React.Fragment key={section.title}>
          <p className="stats-subhead">{section.title}</p>
          <div className="stats-rows">
            {section.rows.map((row) => (
              <div key={row.key} className="stats-rows__item">
                <span className="stats-rows__name">{row.label}</span>
                <span className="stats-rows__num">{row.value}</span>
                <span className="stats-rows__muted">{row.muted}</span>
              </div>
            ))}
          </div>
        </React.Fragment>
      ))}
    </section>
  );
}
